import { IEmployeeAppExperienceInstanceProgress } from '@dadohr/shared/domainTypes/experience';
import styled from '@emotion/styled';
import { Typography, TypographyProps } from '@material-ui/core';
import { formatNumber, toPercent } from '../../../../../../components/Number';
import { useUnsafeTranslation } from '../../../../../../i18next';
import {
  FlexContainer,
  FlexContainerVertical
} from '../../../../../../layout/Flex';
import { WithBreakpoints } from '../Breakpoints';

const BarContainer = styled(FlexContainer)<{ w: string | number }>((p) => ({
  width: p.w,
  '> :first-of-type': {
    borderTopLeftRadius: p.theme.custom.border.radius(0.5),
    borderBottomLeftRadius: p.theme.custom.border.radius(0.5)
  },
  '> :last-child': {
    borderTopRightRadius: p.theme.custom.border.radius(0.5),
    borderBottomRightRadius: p.theme.custom.border.radius(0.5)
  }
}));

const Bar = styled('div')<{ widthPercentage: number }>((p) => ({
  width: `${p.widthPercentage * 100}%`
}));

const GreenBar = styled(Bar)((p) => ({
  backgroundColor: p.theme.custom.colors.green.w400,
  height: p.theme.spacing(1.5)
}));

const RedBar = styled(Bar)((p) => ({
  backgroundColor: p.theme.custom.colors.red.w300,
  height: p.theme.spacing()
}));

const GrayBar = styled(Bar)((p) => ({
  backgroundColor: p.theme.custom.colors.gray.w300,
  height: p.theme.spacing()
}));

const ProgressBar = ({
  d,
  width: maxWidth
}: {
  d: IEmployeeAppExperienceInstanceProgress['counts'];
  width: number | string;
}) => {
  const greenWidth = toPercent(d.completed, d.total) * 100;
  const redWidth = toPercent(d.late, d.total) * 100;
  const grayWidth = toPercent(d.other, d.total) * 100;
  return (
    <BarContainer spacing={0.25} w={maxWidth}>
      {!!d.completed && <GreenBar widthPercentage={greenWidth} />}
      {!!d.late && <RedBar widthPercentage={redWidth} />}
      {!!d.other && <GrayBar widthPercentage={grayWidth} />}
    </BarContainer>
  );
};

export const ProgressTracker = ({
  d,
  noTitle,
  stretch,
  percentageVariant = 'h3',
  barMaxWidth = 200
}: {
  d: IEmployeeAppExperienceInstanceProgress;
  noTitle?: boolean;
  stretch?: boolean;
  percentageVariant?: TypographyProps['variant'];
  barMaxWidth?: number;
}) => {
  const { t } = useUnsafeTranslation();
  if (d.counts.total === 0) {
    return null;
  }
  const title = <strong>{t('My progress')}</strong>;
  const num = (
    <Typography variant={percentageVariant}>
      {formatNumber({ n: d.percentage, format: 'percent', digits: 0 })}
    </Typography>
  );
  return (
    <WithBreakpoints
      lgMd={
        <FlexContainer
          spacing={2}
          fullWidth={stretch}
          justifyContent={stretch ? 'space-between' : 'flex-start'}
        >
          <FlexContainerVertical fullWidth>
            {!noTitle && title}
            <ProgressBar d={d.counts} width={barMaxWidth} />
          </FlexContainerVertical>
          {num}
        </FlexContainer>
      }
      smXs={
        <FlexContainer spacing={2} fullWidth>
          <FlexContainerVertical fullWidth>
            {!noTitle && title}
            <ProgressBar d={d.counts} width={'100%'} />
          </FlexContainerVertical>
          {num}
        </FlexContainer>
      }
    />
  );
};
